import React from 'react';
import { DEFAULT_VALUE_MAP, propertyHideMapping } from 'constants/surveyJS';
import {
  PROPERTY_TYPES,
  SURVEY_PROPERTIES_MAP,
} from 'constants/surveyJS/surveyOwnContants';
import { SurveyCreator } from 'survey-creator-react';
import { Action, ComputedUpdater, ItemValue, SurveyModel } from 'survey-core';
import type {
  ICreatorOptions,
  ModifiedEvent,
  PropertyAddingEvent,
  SurveyCreatorModel,
  SurveyInstanceCreatedEvent,
} from 'survey-creator-core';
import { MAX_FILE_SIZE } from 'constants/index';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { useImageUpload } from 'hooks/blobStorage/useImageUpload';

const creatorOptions: ICreatorOptions = {
  isAutoSave: true,
  showSurveyTitle: false,
  showSidebar: false,
  showJSONEditorTab: true,
  showPreviewTab: false,
};

type SurveyJSInitializer = {
  toolBoxItemsToRemove: string[];
  defaultJSON?: string;
  onclose?: () => void;

  onSurveyUpdate: (
    sender: SurveyCreatorModel,
    options: ModifiedEvent
  ) => Promise<void>;
};

export const useInputFormCreator = ({
  toolBoxItemsToRemove,
  defaultJSON = '',
  onSurveyUpdate,
  onclose,
}: SurveyJSInitializer) => {
  const [creator, setCreator] = React.useState<SurveyCreator | null>(null);
  const { handleFileUpload } = useBlobStorage('public-assets');
  const { handleFileUpload: handleImageUpload } = useImageUpload('public-assets');

  const onShowingPropertyHandler = React.useCallback(
    (surveyCreator: SurveyCreator, options: PropertyAddingEvent) => {
      const propertyType = options.obj.getType();

      if (
        options.property.name === SURVEY_PROPERTIES_MAP.SHOW_QUESTION_NUMBERS
      ) {
        // This is already being done in the load config.
        // Making sure here for older records
        surveyCreator.setPropertyValue(
          SURVEY_PROPERTIES_MAP.SHOW_QUESTION_NUMBERS,
          'off'
        );
      }

      if (
        options.property.name === SURVEY_PROPERTIES_MAP.VALUE_NAME &&
        propertyType !== PROPERTY_TYPES.CHOICE_BY_URL
      ) {
        options.canShow = false;
      }
      options.canShow = !!propertyHideMapping[propertyType]?.includes(
        options.property.name
      );
    },
    []
  );
  const onSurveyInstanceCreatedHandler = React.useCallback(
    (
      surveyCreator: SurveyCreator,
      sender: SurveyCreatorModel,
      options: SurveyInstanceCreatedEvent
    ) => {
      const survey = sender.survey;
      Object.entries(DEFAULT_VALUE_MAP).forEach(([key, value]) => {
        survey[key] = value;
      });
    },
    []
  );

  // JSON object modification handler
  const onModifiedHandler = React.useCallback(
    async (sender: SurveyCreatorModel, options: ModifiedEvent) => {
      if (DEFAULT_VALUE_MAP[options.name || '']) {
        return;
      }
      if (defaultJSON === sender.text) {
        return;
      }
      await onSurveyUpdate(sender, options);
    },
    [defaultJSON, onSurveyUpdate]
  );

  React.useEffect(() => {
    if (creator) {
      return;
    }

    const surveyCreator = new SurveyCreator(creatorOptions);
    if (defaultJSON) {
      surveyCreator.JSON = JSON.parse(defaultJSON);
    }
    // ToolBox Customization
    const toolBox = surveyCreator.toolbox;
    toolBoxItemsToRemove.forEach((item) => toolBox.removeItem(item));
    toolBox.removeCategories();

    //START: top bar customization
    const clearSurveyAction = new Action({
      id: 'icon-v2check',
      title: 'Close',
      showTitle: false,
      iconName: 'icon-v2check',
      location: 'end',
      needSeparator: true,
      visibleIndex: 50,
      visible: new ComputedUpdater(
        () => surveyCreator.activeTab === 'designer'
      ),
      action: () => {
        onclose && onclose();
      },
    });

    // Add a custom button to the top toolbar
    surveyCreator.toolbar.actions.push(clearSurveyAction);

    //END: top bar customization

    surveyCreator.onSurveyInstanceCreated.add((sender, options) =>
      onSurveyInstanceCreatedHandler(surveyCreator, sender, options)
    );
    surveyCreator.onShowingProperty.add((_, options) =>
      onShowingPropertyHandler(surveyCreator, options)
    );

    surveyCreator.onSurveyPropertyValueChanged.add((sender, options) => {
      const obj = options.obj as any;
      if (!(options.propertyName === 'choices' && obj.isQuestion)) {
        return;
      }

      if (ItemValue.getItemByValue(obj.choices, obj.defaultValue)) {
        return;
      }

      if (obj.testValue) {
        obj.testValue = undefined;
      }
      if (obj.defaultValue) {
        obj.defaultValue = undefined;
      }
    });

    surveyCreator.onModified.add(
      async (_, options) => await onModifiedHandler(surveyCreator, options)
    );

    // handle test Value for file upload to blob and set it
    surveyCreator.onPropertyGridShowModal.add((_, options) => {
      if (options.obj.getType() !== 'file') return;
      const popupEditorSurvey = options.popupEditor
        .editSurveyValue as SurveyModel;

      popupEditorSurvey.onUploadFiles.add(async (__, options) => {
        const files = options.files as File[];

        const uploads = await handleFileUpload(files, [], false);
        options.callback(
          files.map((file) => {
            const upload = uploads?.find(
              (upload) => upload.file.name === file.name
            );
            return {
              file: file,
              content: upload?.url,
            };
          })
        );
      });

      const fileQuestionFromPopup = popupEditorSurvey.getAllQuestions()[0];
      fileQuestionFromPopup.storeDataAsText = false;
      fileQuestionFromPopup.allowMultiple = true;
      fileQuestionFromPopup.maxSize = MAX_FILE_SIZE;
      fileQuestionFromPopup.waitForUpload = true;
      fileQuestionFromPopup.acceptedTypes =
        '.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.pdf,.svg';
    });

    surveyCreator.onQuestionAdded.add((_sender, options) => {
      if (options.question.getType() === PROPERTY_TYPES.IMAGE_PICKER) {
        const question = options.question;
        if (question.choices) {
          question.choices = question.choices.filter((choice: any) => {
            return !['Image 1', 'Image 2', 'Image 3', 'Image 4'].includes(
              choice.id
            );
          });
        }
      }
    });

    handleImageUpload &&
      surveyCreator.onUploadFile.add(async (sender, options) => {
        //get the current question to which we are uploading an image
        const currentQuestion = options.question;

        //the files which we browse for survey
        const filesToUpload = options.files;

        //uploading all images to blob app-assets storage
        const uploadedImages = await Promise.all(
          filesToUpload.map(async (file) => {
            const imageLink = await handleImageUpload(file);
            return {
              value: file.name.split('.')[0],
              imageLink,
            };
          })
        );
        if (options.elementType === 'imagepicker') {
          const existingImages = currentQuestion.getPropertyValue('choices');

          currentQuestion.setPropertyValue('choices', [
            ...existingImages,
            ...uploadedImages,
          ]);
        }
        if (options.elementType === 'image') {
          options.callback('success', uploadedImages[0].imageLink);
        }
      });

    setCreator(surveyCreator);
  }, [
    creator,
    defaultJSON,
    toolBoxItemsToRemove,
    onModifiedHandler,
    onSurveyInstanceCreatedHandler,
    onShowingPropertyHandler,
    handleImageUpload,
    handleFileUpload,
    onclose,
  ]);

  return creator;
};
